import Quill from "quill";

/*
  Shared Quill configuration for everything that ends up inside an email.

  - Alignment, font and size are registered as inline styles instead of Quill's default
    CSS classes, because mail clients never see our stylesheet: a class-based format would
    silently disappear for the recipient.
  - Merge fields ({{customer_name}} etc.) are shown as non-editable chips and stored as
    plain {{tokens}} that the backend fills in when the email is sent.
*/

export const FONTS = [
  { value: "Arial", label: "Arial" },
  { value: "Georgia", label: "Georgia" },
  { value: "Helvetica", label: "Helvetica" },
  { value: "Tahoma", label: "Tahoma" },
  { value: "Verdana", label: "Verdana" },
  { value: "Garamond", label: "Garamond" },
  { value: "monospace", label: "Fixed width" },
];

export const SIZES = [
  { value: "13px", label: "Small" },
  { value: "20px", label: "Large" },
  { value: "28px", label: "Huge" },
];

export const MERGE_FIELD_BLOT = "mergeField";
export const MERGE_FIELD_CLASS = "ql-merge-field";
export const TOKEN_PATTERN = /\{\{\s*([a-z_]+)\s*\}\}/g;

let registered = false;

export const registerEmailFormats = () => {
  if (registered) return;
  registered = true;

  const AlignStyle = Quill.import("attributors/style/align");
  const FontStyle = Quill.import("attributors/style/font");
  const SizeStyle = Quill.import("attributors/style/size");
  FontStyle.whitelist = FONTS.map((font) => font.value);
  SizeStyle.whitelist = SIZES.map((size) => size.value);
  Quill.register(AlignStyle, true);
  Quill.register(FontStyle, true);
  Quill.register(SizeStyle, true);

  const Embed = Quill.import("blots/embed");

  class MergeFieldBlot extends Embed {
    static blotName = MERGE_FIELD_BLOT;
    static tagName = "span";
    static className = MERGE_FIELD_CLASS;

    static create(value) {
      const node = super.create(value);
      node.setAttribute("data-field", value.key);
      node.setAttribute("data-label", value.label);
      node.textContent = value.label;
      return node;
    }

    static value(node) {
      return {
        key: node.getAttribute("data-field"),
        label: node.getAttribute("data-label"),
      };
    }

    // What the stored template carries; the backend fills the value in when sending
    html() {
      return `{{${MergeFieldBlot.value(this.domNode).key}}}`;
    }
  }

  Quill.register(MergeFieldBlot, true);
};

const chipElement = (doc, field) => {
  const chip = doc.createElement("span");
  chip.className = MERGE_FIELD_CLASS;
  chip.setAttribute("data-field", field.key);
  chip.setAttribute("data-label", field.label);
  chip.textContent = field.label;
  return chip;
};

// Stored template HTML -> editor HTML: known {{tokens}} in text become chips. Tokens inside
// attributes (an application link's href) are left alone; the backend fills those too.
export const tokensToChips = (html, fields = []) => {
  if (!html) return "";
  const byKey = Object.fromEntries(fields.map((field) => [field.key, field]));
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach((textNode) => {
    const text = textNode.nodeValue;
    if (!TOKEN_PATTERN.test(text)) return;
    TOKEN_PATTERN.lastIndex = 0;

    const fragment = doc.createDocumentFragment();
    let last = 0;
    for (const match of text.matchAll(TOKEN_PATTERN)) {
      const field = byKey[match[1]];
      if (!field) continue;
      if (match.index > last) {
        fragment.appendChild(doc.createTextNode(text.slice(last, match.index)));
      }
      fragment.appendChild(chipElement(doc, field));
      last = match.index + match[0].length;
    }
    if (last < text.length) {
      fragment.appendChild(doc.createTextNode(text.slice(last)));
    }
    textNode.parentNode.replaceChild(fragment, textNode);
  });

  return doc.body.innerHTML;
};

const isChip = (node) =>
  node?.nodeType === Node.ELEMENT_NODE &&
  node.classList.contains(MERGE_FIELD_CLASS);

// The chip the caret is touching: inside one of its hidden caret guards, or right beside it
const chipAtCaret = ({ anchorNode: node, anchorOffset: offset }) => {
  if (node?.nodeType === Node.TEXT_NODE) {
    return isChip(node.parentElement) ? node.parentElement : null;
  }
  const before = node?.childNodes[offset - 1];
  const after = node?.childNodes[offset];
  if (isChip(before)) return before;
  return isChip(after) ? after : null;
};

// Chrome types a space after a chip at the end of a line into the chip's hidden caret guard,
// then leaves the caret in front of that space, so the next word lands against the chip.
// Text typed right beside a chip is inserted through Quill instead.
export const typeBesideChips = (quill) => {
  quill.root.addEventListener("beforeinput", (event) => {
    if (event.inputType !== "insertText" || !event.data || event.isComposing) {
      return;
    }
    const selection = document.getSelection();
    const chip = selection?.isCollapsed ? chipAtCaret(selection) : null;
    const range = chip && quill.getSelection();
    if (!range) return;
    event.preventDefault();
    // The text takes the chip's formatting, so bold stays bold, but never extends a link
    const chipIndex = Quill.find(chip).offset(quill.scroll);
    const { link, ...formats } = quill.getFormat(chipIndex, 1);
    quill.insertText(range.index, event.data, formats, "user");
    quill.setSelection(range.index + event.data.length, 0, "user");
  });
};

// Stored subject -> editor contents: known {{tokens}} become chips, the rest stays plain text
export const subjectToOps = (subject, fields = []) => {
  const text = subject ?? "";
  const byKey = Object.fromEntries(fields.map((field) => [field.key, field]));
  const ops = [];
  let last = 0;
  for (const match of text.matchAll(TOKEN_PATTERN)) {
    const field = byKey[match[1]];
    if (!field) continue;
    if (match.index > last) ops.push({ insert: text.slice(last, match.index) });
    ops.push({
      insert: { [MERGE_FIELD_BLOT]: { key: field.key, label: field.label } },
    });
    last = match.index + match[0].length;
  }
  if (last < text.length) ops.push({ insert: text.slice(last) });
  return ops;
};

// Editor contents -> subject to store: chips become {{tokens}} again, and the subject stays
// on one line (Quill always ends its contents with a line break)
export const opsToSubject = (ops = []) =>
  ops
    .map((op) => {
      if (typeof op.insert === "string") return op.insert;
      const field = op.insert?.[MERGE_FIELD_BLOT];
      return field ? `{{${field.key}}}` : "";
    })
    .join("")
    .replace(/\n$/, "")
    .replace(/\n/g, " ");

// Editor -> HTML to store or send. getSemanticHTML() gives real <ul>/<ol> lists and lets
// chips serialize themselves as {{tokens}}, but it also turns every space into &nbsp;
// (which would stop the text wrapping in a mail client) and writes a blank line as an
// empty <p>, which mail clients collapse to nothing.
export const quillToHtml = (quill) => {
  if (!quill) return "";
  return quill
    .getSemanticHTML()
    .replace(/&nbsp;/g, " ")
    .replace(/&#39;/g, "'")
    .replace(/<(p|li|blockquote)([^>]*)><\/\1>/g, "<$1$2><br></$1>");
};

// True when the editor holds nothing but whitespace (U+FEFF is Quill's embed guard character)
export const isEditorEmpty = (quill) =>
  !quill || quill.getText().replace(/\uFEFF/g, "").trim() === "";
